/**
 * wizardState.js — versión 2b-bis: añade `ambito` al payload + action
 * `CAMBIAR_AMBITO` que limpia técnicos y usuarios (porque cambian de tabla).
 */

export const TOTAL_PASOS = 7;

export const initialState = {
  payload: {
    // Paso 1
    titulo: '',
    descripcion: '',
    titulo_usuario: '',                 // Fase 15 (#5) — contenido público (opcional)
    descripcion_usuario: '',            // Fase 15 (#5)
    ambito: 'interno',                  // NUEVO ('interno' | 'colaborador')
    asignacion_modo: 'libre',
    max_cancelaciones_default: 3,
    horas_antelacion_cancelar: 0,       // Fase 3b

    // Paso 2
    subtareas: [],

    // Paso 3
    fecha_inicio: '',
    fecha_limite: '',
    horario_inicio: '09:00',     // legacy/fallback (ya no editable; ver Paso3 franjas)
    horario_fin: '14:00',        // legacy/fallback
    duracion_cita: 30,
    franjas: [],                 // Fase 12b: [{ dia_semana, hora_inicio, hora_fin, orden }]
    // Fase 19a · #17 — permite_fin_semana es la fuente de verdad (se persiste).
    // excluir_fines_de_semana se mantiene como campo DERIVADO (lo consumen Paso4
    // y el preview de Paso7). Se sincronizan vía TOGGLE_PERMITE_FIN_SEMANA / LOAD.
    permite_fin_semana: false,
    excluir_fines_de_semana: true,
    excluir_festivos: true,
    // Fase 22 · #21 — recordatorios a usuarios sin cita (días laborables antes
    // de fecha_limite). null = no enviar.
    recordatorio_1_dias_lab_antes: null,
    recordatorio_2_dias_lab_antes: null,

    // Paso 4
    excluidos_manuales: [],
    // hotfix/dias-excluidos — el servidor SOLO hace caso de excluidos_manuales si llega esta marca
    // (un front viejo los mandaba siempre vacíos y cada guardado en Editar reabría los días). En una
    // campaña nueva la lista del asistente es la buena: true. Al cargar, true solo si el servidor los
    // devolvió (ver LOAD).
    excluidos_cargados: true,

    // Pasos 5-6
    tecnicos_ids: [],
    usuarios_ids: [],
    _tecnicos_cache: {},
    _usuarios_cache: {},
  },
  paso: 1,
  errores: [],
  guardando: false,
  id: null,
};

export function wizardReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD': {
      return {
        ...state,
        payload: { ...state.payload, [action.field]: action.value },
        errores: state.errores.filter((e) => e.campo !== action.field),
      };
    }

    case 'SET_MULTI': {
      return {
        ...state,
        payload: { ...state.payload, ...action.values },
        errores: state.errores.filter(
          (e) => !Object.keys(action.values).includes(e.campo)
        ),
      };
    }

    case 'SET_PASO': {
      const paso = Math.max(1, Math.min(TOTAL_PASOS, action.paso));
      return { ...state, paso };
    }

    case 'SET_ERRORES':
      return { ...state, errores: action.errores || [] };

    case 'SET_GUARDANDO':
      return { ...state, guardando: !!action.value };

    case 'SET_ID':
      return { ...state, id: action.id };

    // ÁMBITO: al cambiar, hay que limpiar técnicos y usuarios porque son IDs
    // que apuntan a una tabla distinta según el ámbito.
    case 'CAMBIAR_AMBITO': {
      if (state.payload.ambito === action.ambito) return state;
      return {
        ...state,
        payload: {
          ...state.payload,
          ambito: action.ambito,
          tecnicos_ids: [],
          usuarios_ids: [],
          _tecnicos_cache: {},
          _usuarios_cache: {},
        },
        errores: state.errores.filter(
          (e) => !['ambito', 'tecnicos_ids', 'usuarios_ids'].includes(e.campo)
        ),
      };
    }

    case 'SUBTAREA_ADD': {
      const nuevas = [
        ...state.payload.subtareas,
        { nombre: '', descripcion: '', orden: state.payload.subtareas.length + 1 },
      ];
      return { ...state, payload: { ...state.payload, subtareas: nuevas } };
    }

    case 'SUBTAREA_UPDATE': {
      const nuevas = state.payload.subtareas.map((s, i) =>
        i === action.index ? { ...s, [action.field]: action.value } : s
      );
      return { ...state, payload: { ...state.payload, subtareas: nuevas } };
    }

    case 'SUBTAREA_REMOVE': {
      const nuevas = state.payload.subtareas
        .filter((_, i) => i !== action.index)
        .map((s, i) => ({ ...s, orden: i + 1 }));
      return { ...state, payload: { ...state.payload, subtareas: nuevas } };
    }

    case 'SUBTAREA_MOVE': {
      const list = [...state.payload.subtareas];
      const { from, to } = action;
      if (from < 0 || to < 0 || from >= list.length || to >= list.length) return state;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      const renumerada = list.map((s, i) => ({ ...s, orden: i + 1 }));
      return { ...state, payload: { ...state.payload, subtareas: renumerada } };
    }

    // Tras guardar, el servidor devuelve { id, orden } de cada subtarea en el orden en que se
    // enviaron: las nuevas reciben su id, y el siguiente guardado las actualiza en vez de duplicarlas.
    // Si la lista ya no cuadra (no debería: el formulario está bloqueado mientras guarda), no se toca.
    case 'SUBTAREAS_IDS': {
      const guardadas = Array.isArray(action.subtareas) ? action.subtareas : [];
      if (guardadas.length !== state.payload.subtareas.length) return state;
      const nuevas = state.payload.subtareas.map((s, i) => ({ ...s, id: Number(guardadas[i].id) }));
      return { ...state, payload: { ...state.payload, subtareas: nuevas } };
    }

    case 'TOGGLE_DIA_EXCLUIDO': {
      const set = new Set(state.payload.excluidos_manuales);
      if (set.has(action.fecha)) set.delete(action.fecha);
      else set.add(action.fecha);
      return {
        ...state,
        payload: { ...state.payload, excluidos_manuales: Array.from(set).sort() },
      };
    }

    case 'CLEAR_DIAS_EXCLUIDOS':
      return {
        ...state,
        payload: { ...state.payload, excluidos_manuales: [] },
      };

    // Fase 19a · #17 — actualiza fuente (permite_fin_semana) y derivado
    // (excluir_fines_de_semana) atómicamente, evitando 2 dispatches y renders
    // intermedios inconsistentes.
    case 'TOGGLE_PERMITE_FIN_SEMANA': {
      const permite = !!action.value;
      return {
        ...state,
        payload: {
          ...state.payload,
          permite_fin_semana: permite,
          excluir_fines_de_semana: !permite,
        },
      };
    }

    case 'PERSONA_ADD': {
      const { rol, persona } = action;
      const idsKey = rol === 'tecnico' ? 'tecnicos_ids' : 'usuarios_ids';
      const cacheKey = rol === 'tecnico' ? '_tecnicos_cache' : '_usuarios_cache';
      if (state.payload[idsKey].includes(persona.id)) return state;
      return {
        ...state,
        payload: {
          ...state.payload,
          [idsKey]: [...state.payload[idsKey], persona.id],
          [cacheKey]: { ...state.payload[cacheKey], [persona.id]: persona },
        },
        errores: state.errores.filter((e) => e.campo !== idsKey),
      };
    }

    case 'PERSONA_REMOVE': {
      const { rol, id } = action;
      const idsKey = rol === 'tecnico' ? 'tecnicos_ids' : 'usuarios_ids';
      const cacheKey = rol === 'tecnico' ? '_tecnicos_cache' : '_usuarios_cache';
      const cache = { ...state.payload[cacheKey] };
      delete cache[id];
      return {
        ...state,
        payload: {
          ...state.payload,
          [idsKey]: state.payload[idsKey].filter((x) => x !== id),
          [cacheKey]: cache,
        },
      };
    }

    case 'LOAD': {
      const c = action.campana || {};
      const tecnicosArr = Array.isArray(c.tecnicos) ? c.tecnicos : [];
      const usuariosArr = Array.isArray(c.usuarios) ? c.usuarios : [];
      // Fase 16c: el cache incluye negocio para agrupar en la zona "Seleccionados"
      // al editar (getCampanaById ya devuelve negocio_id/negocio; ver 16b).
      const tecCache = {};
      tecnicosArr.forEach((t) => {
        tecCache[t.id] = {
          id: t.id, nombre_completo: t.nombre,
          negocio_id: t.negocio_id ?? null, negocio: t.negocio ?? null,
        };
      });
      const usrCache = {};
      usuariosArr.forEach((u) => {
        usrCache[u.persona_id] = {
          id: u.persona_id, nombre_completo: u.nombre,
          negocio_id: u.negocio_id ?? null, negocio: u.negocio ?? null,
        };
      });
      // Fase 12b: el backend devuelve franjas agrupadas por día ({ "1": [...] }).
      // El componente las usa como array plano [{ dia_semana, hora_inicio, ... }].
      const franjasPorDia = c.franjas || {};
      const franjasAplanadas = [];
      for (const dia of [1, 2, 3, 4, 5, 6, 7]) {
        const lista = franjasPorDia[dia] || [];
        lista.forEach((f, idx) => {
          franjasAplanadas.push({
            dia_semana: dia,
            hora_inicio: f.hora_inicio,
            hora_fin: f.hora_fin,
            orden: typeof f.orden === 'number' ? f.orden : idx,
          });
        });
      }
      return {
        ...state,
        id: c.id || null,
        payload: {
          ...initialState.payload,
          titulo: c.titulo || '',
          descripcion: c.descripcion || '',
          titulo_usuario: c.titulo_usuario || '',           // Fase 15 (#5)
          descripcion_usuario: c.descripcion_usuario || '', // Fase 15 (#5)
          ambito: c.ambito || 'interno',  // cargar ambito
          asignacion_modo: c.asignacion_modo || 'libre',
          max_cancelaciones_default:
            typeof c.max_cancelaciones_default === 'number'
              ? c.max_cancelaciones_default
              : 3,
          horas_antelacion_cancelar: c.horas_antelacion_cancelar || 0,
          // El id viaja de vuelta al guardar: el servidor actualiza esa subtarea en vez de borrarla
          // y recrearla, y así las marcas de «hecha» de las citas siguen apuntando a ella (31/10).
          subtareas: Array.isArray(c.subtareas)
            ? c.subtareas.map((s, i) => ({
                ...(s.id != null ? { id: Number(s.id) } : {}),
                nombre: s.nombre || '',
                descripcion: s.descripcion || '',
                orden: s.orden || i + 1,
              }))
            : [],
          // El backend puede devolver las fechas como ISO con hora/TZ
          // (p.ej. "2026-07-31T00:00:00.000Z"), que <input type="date"> NO
          // acepta y deja el campo vacío (Bug 3). Normalizamos a YYYY-MM-DD.
          fecha_inicio: (c.fecha_inicio || '').slice(0, 10),
          fecha_limite: (c.fecha_limite || '').slice(0, 10),
          horario_inicio: c.horario_inicio || '09:00',
          horario_fin: c.horario_fin || '14:00',
          duracion_cita: c.duracion_cita || 30,
          franjas: franjasAplanadas,
          // Fase 19a · #17 — cargar desde la campaña (arregla bug latente: antes
          // forzaba excluir_fines_de_semana:true ignorando la campaña, por lo que
          // al editar una campaña con finde activado se reseteaba). `?? false` =
          // retrocompat con campañas anteriores a la migración (sin el campo).
          permite_fin_semana: c.permite_fin_semana ?? false,
          excluir_fines_de_semana: !(c.permite_fin_semana ?? false),
          excluir_festivos: true,
          // Fase 22 · #21 — cargar config de recordatorios (null = no enviar).
          recordatorio_1_dias_lab_antes: c.recordatorio_1_dias_lab_antes ?? null,
          recordatorio_2_dias_lab_antes: c.recordatorio_2_dias_lab_antes ?? null,
          // hotfix/dias-excluidos — las exclusiones a mano no tienen columna: el servidor las
          // reconstruye y las devuelve. Antes se ponían a [] y al guardar se reabrían los días. Si el
          // servidor no las trae (uno viejo), [] y SIN la marca: así no se toman por «ninguna».
          excluidos_manuales: Array.isArray(c.excluidos_manuales) ? [...c.excluidos_manuales].sort() : [],
          excluidos_cargados: Array.isArray(c.excluidos_manuales),
          tecnicos_ids: tecnicosArr.map((t) => t.id),
          usuarios_ids: usuariosArr.map((u) => u.persona_id),
          _tecnicos_cache: tecCache,
          _usuarios_cache: usrCache,
        },
        errores: [],
      };
    }

    default:
      return state;
  }
}

export function buildPayload(state, { estado }) {
  const p = { ...state.payload };
  delete p._tecnicos_cache;
  delete p._usuarios_cache;
  if (estado) p.estado = estado;
  return p;
}

// Rediseño (Fase 2) · A · duración libre: un entero de minutos, con los MISMOS límites que el servidor
// (campanasValidator: «entero entre 5 y 480»). Ojo: si llega vacía, el servidor no la valida y conserva
// la anterior sin decir nada; por eso el formulario no deja guardar sin una duración válida.
export const DURACION_MIN = 5;
export const DURACION_MAX = 480;

export function duracionValida(d) {
  return Number.isInteger(d) && d >= DURACION_MIN && d <= DURACION_MAX;
}

/** Lo escrito en el campo, como número: '' → null; «20» → 20; lo que no es un entero → null. */
export function leerDuracion(raw) {
  const t = String(raw ?? '').trim();
  if (!/^\d+$/.test(t)) return null;
  return parseInt(t, 10);
}

export function pasoEsValidoLocal(paso, payload) {
  switch (paso) {
    case 1:
      return !!(payload.titulo || '').trim() && !!payload.ambito;
    case 2:
      return payload.subtareas.every((s) => (s.nombre || '').trim());
    case 3: {
      // Fase 12b: el paso 3 valida fechas + duración + franjas por día.
      if (!payload.fecha_inicio || !payload.fecha_limite) return false;
      if (payload.fecha_inicio > payload.fecha_limite) return false;
      if (!duracionValida(payload.duracion_cita)) return false;
      const fr = payload.franjas;
      if (!Array.isArray(fr) || fr.length === 0) return false;
      // Conversión a minutos (tolera "HH:MM" y "HH:MM:SS").
      const toMin = (t) => {
        if (!t) return NaN;
        const [h, m] = String(t).split(':').map((n) => parseInt(n, 10));
        return (h || 0) * 60 + (m || 0);
      };
      for (const f of fr) {
        if (!f.dia_semana || f.dia_semana < 1 || f.dia_semana > 7) return false;
        if (!f.hora_inicio || !f.hora_fin) return false;
        if (toMin(f.hora_inicio) >= toMin(f.hora_fin)) return false;
      }
      // Sin solapes dentro del mismo día.
      const porDia = {};
      for (const f of fr) {
        (porDia[f.dia_semana] = porDia[f.dia_semana] || []).push(f);
      }
      for (const dia of Object.keys(porDia)) {
        const lista = porDia[dia]
          .slice()
          .sort((a, b) => toMin(a.hora_inicio) - toMin(b.hora_inicio));
        for (let i = 1; i < lista.length; i++) {
          if (toMin(lista[i].hora_inicio) < toMin(lista[i - 1].hora_fin)) return false;
        }
      }
      return true;
    }
    case 4:
      return true;
    case 5:
      return payload.tecnicos_ids.length > 0;
    case 6:
      return payload.usuarios_ids.length > 0;
    case 7:
      return true;
    default:
      return false;
  }
}
